import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { ILead, LeadStatus } from "../../../Api/Interface/api.interface";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import ViewDayIcon from "@mui/icons-material/ViewDay";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import EventIcon from "@mui/icons-material/Event";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

// ── Constants ──────────────────────────────────────────────────────────────────
const BORDER = "#e2e8f0";
const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const STATUS_CONFIG: Record<LeadStatus, { label: string; color: string; bg: string; border: string; dot: string }> = {
  new:       { label: "New",       color: "#2f3891", bg: "#eef2ff", border: "#c7d2fe", dot: "#2f3891" },
  contacted: { label: "Contacted", color: "#0ea5e9", bg: "#f0f9ff", border: "#bae6fd", dot: "#0ea5e9" },
  qualified: { label: "Qualified", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", dot: "#f59e0b" },
  lost:      { label: "Lost",      color: "#ef4444", bg: "#fef2f2", border: "#fecaca", dot: "#ef4444" },
  converted: { label: "Converted", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", dot: "#16a34a" },
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  school:    "School",
  wedding:   "Wedding",
  corporate: "Corporate",
  other:     "Other",
};

type CalView = "month" | "week" | "day" | "list";

// ── Event chip ─────────────────────────────────────────────────────────────────
const EventChip: React.FC<{ lead: ILead; onClick: () => void; compact?: boolean }> = ({ lead, onClick, compact }) => {
  const st = STATUS_CONFIG[lead.status];
  const name = `${lead.contact.firstname}${lead.contact.lastname ? " " + lead.contact.lastname : ""}`;
  const eventType = lead.contact.event_type ? EVENT_TYPE_LABELS[lead.contact.event_type] ?? lead.contact.event_type : null;

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{name}</Typography>
          {eventType && <Typography sx={{ fontSize: 11 }}>{eventType}</Typography>}
          {lead.contact.route_pickup && lead.contact.route_dropoff && (
            <Typography sx={{ fontSize: 11 }}>{lead.contact.route_pickup} → {lead.contact.route_dropoff}</Typography>
          )}
          <Typography sx={{ fontSize: 11 }}>{st.label}</Typography>
        </Box>
      }
    >
      <Box
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        sx={{
          px: 0.75, py: 0.2,
          bgcolor: st.bg, border: `1px solid ${st.border}`, borderRadius: 1,
          cursor: "pointer", mb: 0.3, overflow: "hidden",
          "&:hover": { filter: "brightness(0.96)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: st.dot, flexShrink: 0 }} />
          <Typography
            noWrap
            sx={{ fontSize: compact ? 9 : 10, fontWeight: 600, color: st.color, lineHeight: 1.4 }}
          >
            {name}{eventType ? ` · ${eventType}` : ""}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

// ── Day cell ───────────────────────────────────────────────────────────────────
const DayCell: React.FC<{
  date: Dayjs;
  isCurrentMonth: boolean;
  isToday: boolean;
  leads: ILead[];
  onLeadClick: (id: number) => void;
  onDayClick: (d: Dayjs) => void;
}> = ({ date, isCurrentMonth, isToday, leads, onLeadClick, onDayClick }) => {
  const MAX_VISIBLE = 3;
  const visible = leads.slice(0, MAX_VISIBLE);
  const overflow = leads.length - MAX_VISIBLE;

  return (
    <Box
      onClick={() => onDayClick(date)}
      sx={{
        minHeight: 90, p: 0.5, borderRight: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`,
        bgcolor: isToday ? "#eff6ff" : isCurrentMonth ? "#ffffff" : "#f8fafc",
        cursor: "pointer", position: "relative", overflow: "hidden",
        "&:hover": { bgcolor: isToday ? "#dbeafe" : "#f1f5f9" },
        transition: "background 0.1s",
      }}
    >
      {/* Day number */}
      <Box
        sx={{
          width: 22, height: 22, borderRadius: "50%",
          bgcolor: isToday ? "#2563eb" : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center", mb: 0.25,
        }}
      >
        <Typography
          sx={{
            fontSize: 11, fontWeight: isToday ? 800 : isCurrentMonth ? 600 : 400,
            color: isToday ? "#ffffff" : isCurrentMonth ? "#1e293b" : "#94a3b8",
            lineHeight: 1,
          }}
        >
          {date.date()}
        </Typography>
      </Box>

      {/* Events */}
      {visible.map((lead) => (
        <EventChip key={lead.id} lead={lead} onClick={() => onLeadClick(lead.id)} compact />
      ))}
      {overflow > 0 && (
        <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#2f3891", pl: 0.5 }}>
          +{overflow} more
        </Typography>
      )}
    </Box>
  );
};

// ── Main Calendar component ────────────────────────────────────────────────────
interface CalendarViewProps {
  leads: ILead[];
  isLoading: boolean;
  onLeadClick: (id: number) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ leads, isLoading, onLeadClick }) => {
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [view, setView] = useState<CalView>("month");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "">("");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("");

  const today = dayjs();

  // Use event_date if set, otherwise fall back to created_at
  const getLeadDate = (l: ILead): Dayjs =>
    l.contact.event_date ? dayjs(l.contact.event_date) : dayjs(l.created_at);

  const calendarLeads = useMemo(() => {
    return leads.filter((l) => {
      if (statusFilter && l.status !== statusFilter) return false;
      if (eventTypeFilter && l.contact.event_type !== eventTypeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const name = `${l.contact.firstname} ${l.contact.lastname || ""}`.toLowerCase();
        if (!name.includes(q) && !l.contact.email?.toLowerCase().includes(q) && !l.lead_ref.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [leads, statusFilter, eventTypeFilter, search]);

  // Get leads for a specific date
  const leadsForDate = (date: Dayjs) =>
    calendarLeads.filter((l) => getLeadDate(l).isSame(date, "day"));

  // ── Navigation ───────────────────────────────────────────────────────────────
  const navigate = (dir: -1 | 1) => {
    if (view === "month") setCurrentDate((d) => d.add(dir, "month"));
    else if (view === "week") setCurrentDate((d) => d.add(dir * 7, "day"));
    else setCurrentDate((d) => d.add(dir, "day"));
  };

  const headerTitle = () => {
    if (view === "month") return currentDate.format("MMMM YYYY");
    if (view === "week") {
      const start = currentDate.startOf("week");
      const end = start.add(6, "day");
      return start.month() === end.month()
        ? `${start.format("MMM D")} – ${end.format("D, YYYY")}`
        : `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
    }
    return currentDate.format("dddd, MMMM D, YYYY");
  };

  // ── Month grid cells ─────────────────────────────────────────────────────────
  const monthCells = useMemo(() => {
    const start = currentDate.startOf("month");
    const end = currentDate.endOf("month");
    const cells: { date: Dayjs; isCurrentMonth: boolean }[] = [];

    for (let i = 0; i < start.day(); i++) {
      cells.push({ date: start.subtract(start.day() - i, "day"), isCurrentMonth: false });
    }
    for (let d = 1; d <= end.date(); d++) {
      cells.push({ date: start.date(d), isCurrentMonth: true });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ date: cells[cells.length - 1].date.add(1, "day"), isCurrentMonth: false });
    }
    return cells;
  }, [currentDate]);

  // ── Week days ────────────────────────────────────────────────────────────────
  const weekDays = useMemo(() => {
    const start = currentDate.startOf("week");
    return Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
  }, [currentDate]);

  // ── List: leads sorted by date ───────────────────────────────────────────────
  const sortedListLeads = useMemo(() => {
    return [...calendarLeads].sort((a, b) =>
      getLeadDate(a).diff(getLeadDate(b))
    );
  }, [calendarLeads]);

  // ── View buttons config ──────────────────────────────────────────────────────
  const viewBtns: { key: CalView; icon: React.ReactNode; label: string }[] = [
    { key: "month", icon: <CalendarViewMonthIcon sx={{ fontSize: 15 }} />, label: "Month" },
    { key: "week",  icon: <ViewWeekIcon sx={{ fontSize: 15 }} />,          label: "Week"  },
    { key: "day",   icon: <ViewDayIcon sx={{ fontSize: 15 }} />,           label: "Day"   },
    { key: "list",  icon: <FormatListBulletedIcon sx={{ fontSize: 15 }} />, label: "List" },
  ];

  // ── List row ─────────────────────────────────────────────────────────────────
  const ListRow: React.FC<{ lead: ILead }> = ({ lead }) => {
    const st = STATUS_CONFIG[lead.status];
    const name = `${lead.contact.firstname}${lead.contact.lastname ? " " + lead.contact.lastname : ""}`;
    const eventType = lead.contact.event_type ? EVENT_TYPE_LABELS[lead.contact.event_type] : null;
    const leadDate = getLeadDate(lead);
    const isPast = leadDate.isBefore(today, "day");
    const hasEventDate = !!lead.contact.event_date;

    return (
      <Box
        onClick={() => onLeadClick(lead.id)}
        sx={{
          display: "flex", alignItems: "center", gap: 2, px: 3, py: 1.5,
          borderBottom: `1px solid #f1f5f9`, cursor: "pointer",
          bgcolor: isPast ? "#f8fafc" : "#ffffff",
          "&:hover": { bgcolor: "#eff6ff" },
        }}
      >
        {/* Date */}
        <Box sx={{ textAlign: "center", minWidth: 48, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: isPast ? "#94a3b8" : "#1e293b", lineHeight: 1 }}>
            {leadDate.format("D")}
          </Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>
            {leadDate.format("MMM")}
          </Typography>
          <Typography sx={{ fontSize: 10, color: hasEventDate ? "#cbd5e1" : "#f59e0b" }}>
            {hasEventDate ? leadDate.format("YYYY") : "enquiry"}
          </Typography>
        </Box>

        {/* Color bar */}
        <Box sx={{ width: 4, height: 40, bgcolor: st.dot, borderRadius: 2, flexShrink: 0 }} />

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{name}</Typography>
            {eventType && (
              <Box sx={{ px: 0.75, py: 0.15, bgcolor: "#f1f5f9", borderRadius: 1, border: `1px solid #e2e8f0` }}>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#64748b" }}>{eventType}</Typography>
              </Box>
            )}
            <Box sx={{ px: 0.75, py: 0.15, bgcolor: st.bg, borderRadius: 1, border: `1px solid ${st.border}` }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: st.color }}>{st.label}</Typography>
            </Box>
          </Box>
          {(lead.contact.route_pickup || lead.contact.route_dropoff) && (
            <Typography sx={{ fontSize: 11, color: "#64748b" }} noWrap>
              {lead.contact.route_pickup}{lead.contact.route_dropoff ? ` → ${lead.contact.route_dropoff}` : ""}
            </Typography>
          )}
          {lead.contact.email && (
            <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>{lead.contact.email}</Typography>
          )}
        </Box>

        {/* Ref */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#2f3891", fontFamily: "monospace", flexShrink: 0 }}>
          {lead.lead_ref}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      {/* ── Filter bar ── */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <TextField
          size="small" placeholder="Search name, email, ref..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "#94a3b8" }} /></InputAdornment> }}
          sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
        />
        <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as LeadStatus | "")} displayEmpty
          sx={{ minWidth: 140, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff" }}>
          <MenuItem value=""><em>All Statuses</em></MenuItem>
          {(Object.keys(STATUS_CONFIG) as LeadStatus[]).map((s) => (
            <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_CONFIG[s].dot }} />
                {STATUS_CONFIG[s].label}
              </Box>
            </MenuItem>
          ))}
        </Select>
        <Select size="small" value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)} displayEmpty
          sx={{ minWidth: 140, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff" }}>
          <MenuItem value=""><em>All Types</em></MenuItem>
          {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => (
            <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>{v}</MenuItem>
          ))}
        </Select>
        {(search || statusFilter || eventTypeFilter) && (
          <Button size="small" onClick={() => { setSearch(""); setStatusFilter(""); setEventTypeFilter(""); }}
            sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", border: `1px solid ${BORDER}` }}>
            Clear
          </Button>
        )}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981" }} />
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>
            {calendarLeads.length} enquir{calendarLeads.length === 1 ? "y" : "ies"}
          </Typography>
        </Box>
      </Box>

      {/* ── Calendar header ── */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap",
        gap: 1, mb: 1.5, px: 2, py: 1.25,
        bgcolor: "#ffffff", border: `1px solid ${BORDER}`, borderRadius: 2,
      }}>
        {/* Navigation */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button size="small" variant="outlined" onClick={() => setCurrentDate(today)}
            startIcon={<TodayIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none", fontSize: 12, borderRadius: 1.5, height: 32, borderColor: BORDER, color: "#374151" }}>
            Today
          </Button>
          <Box sx={{ display: "flex", border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: "hidden" }}>
            <Box component="button" onClick={() => navigate(-1)}
              sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", px: 1, py: 0.5, display: "flex", alignItems: "center",
                "&:hover": { bgcolor: "#f1f5f9" } }}>
              <ChevronLeftIcon sx={{ fontSize: 18, color: "#374151" }} />
            </Box>
            <Box component="button" onClick={() => navigate(1)}
              sx={{ border: "none", borderLeft: `1px solid ${BORDER}`, bgcolor: "transparent", cursor: "pointer", px: 1, py: 0.5, display: "flex", alignItems: "center",
                "&:hover": { bgcolor: "#f1f5f9" } }}>
              <ChevronRightIcon sx={{ fontSize: 18, color: "#374151" }} />
            </Box>
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", ml: 1 }}>{headerTitle()}</Typography>
        </Box>

        {/* View switcher */}
        <Box sx={{ display: "flex", border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: "hidden" }}>
          {viewBtns.map((v) => (
            <Button key={v.key} size="small" startIcon={v.icon}
              onClick={() => setView(v.key)}
              sx={{
                textTransform: "none", fontSize: 12, borderRadius: 0, height: 32, px: 1.5,
                borderRight: v.key !== "list" ? `1px solid ${BORDER}` : "none",
                bgcolor: view === v.key ? "#eff6ff" : "transparent",
                color: view === v.key ? "#2563eb" : "#374151",
                fontWeight: view === v.key ? 700 : 400,
                "&:hover": { bgcolor: "#f1f5f9" },
              }}>
              {v.label}
            </Button>
          ))}
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={36} />
        </Box>
      ) : (
        <>
          {/* ── Month view ── */}
          {view === "month" && (
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden", bgcolor: "#ffffff" }}>
              {/* Day headers */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", bgcolor: "#f8fafc", borderBottom: `1px solid ${BORDER}` }}>
                {DAYS_OF_WEEK.map((d) => (
                  <Box key={d} sx={{ py: 0.75, textAlign: "center", borderRight: `1px solid ${BORDER}`, "&:last-child": { borderRight: "none" } }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>{d}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Cells grid */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                {monthCells.map((cell, i) => {
                  const cellLeads = leadsForDate(cell.date);
                  const isToday = cell.date.isSame(today, "day");
                  const isLastCol = (i + 1) % 7 === 0;
                  return (
                    <Box key={i} sx={{ borderRight: isLastCol ? "none" : `1px solid ${BORDER}` }}>
                      <DayCell
                        date={cell.date}
                        isCurrentMonth={cell.isCurrentMonth}
                        isToday={isToday}
                        leads={cellLeads}
                        onLeadClick={onLeadClick}
                        onDayClick={(d) => { setCurrentDate(d); setView("day"); }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── Week view ── */}
          {view === "week" && (
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden", bgcolor: "#ffffff" }}>
              {/* Header row */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${BORDER}` }}>
                {weekDays.map((d, i) => {
                  const isToday = d.isSame(today, "day");
                  return (
                    <Box key={i} onClick={() => { setCurrentDate(d); setView("day"); }}
                      sx={{
                        py: 1, textAlign: "center", cursor: "pointer",
                        borderRight: i < 6 ? `1px solid ${BORDER}` : "none",
                        bgcolor: isToday ? "#eff6ff" : "#f8fafc",
                        "&:hover": { bgcolor: "#e0f2fe" },
                      }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {d.format("ddd")}
                      </Typography>
                      <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: isToday ? "#2563eb" : "transparent", mx: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: 14, fontWeight: isToday ? 800 : 600, color: isToday ? "#ffffff" : "#1e293b" }}>
                          {d.date()}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Events per day */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                {weekDays.map((d, i) => {
                  const dayLeads = leadsForDate(d);
                  return (
                    <Box key={i}
                      sx={{
                        minHeight: 120, p: 0.75,
                        borderRight: i < 6 ? `1px solid ${BORDER}` : "none",
                        bgcolor: d.isSame(today, "day") ? "#fafbff" : "#ffffff",
                      }}>
                      {dayLeads.length === 0 ? (
                        <Typography sx={{ fontSize: 10, color: "#e2e8f0", textAlign: "center", mt: 2 }}>—</Typography>
                      ) : dayLeads.map((l) => (
                        <EventChip key={l.id} lead={l} onClick={() => onLeadClick(l.id)} compact />
                      ))}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── Day view ── */}
          {view === "day" && (
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden", bgcolor: "#ffffff" }}>
              <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${BORDER}`, bgcolor: currentDate.isSame(today, "day") ? "#eff6ff" : "#f8fafc" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                  {currentDate.format("dddd, MMMM D, YYYY")}
                </Typography>
              </Box>
              {(() => {
                const dayLeads = leadsForDate(currentDate);
                return dayLeads.length === 0 ? (
                  <Box sx={{ py: 8, textAlign: "center" }}>
                    <EventIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                    <Typography sx={{ fontSize: 14, color: "#94a3b8", fontWeight: 600 }}>No enquiries on this day</Typography>
                    <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>Enquiries with this event date or received on this day will appear here.</Typography>
                  </Box>
                ) : dayLeads.map((lead) => {
                  const st = STATUS_CONFIG[lead.status];
                  const name = `${lead.contact.firstname}${lead.contact.lastname ? " " + lead.contact.lastname : ""}`;
                  const eventType = lead.contact.event_type ? EVENT_TYPE_LABELS[lead.contact.event_type] : null;
                  return (
                    <Box key={lead.id} onClick={() => onLeadClick(lead.id)}
                      sx={{
                        display: "flex", alignItems: "flex-start", gap: 2, px: 3, py: 2,
                        borderBottom: `1px solid #f1f5f9`, cursor: "pointer",
                        "&:hover": { bgcolor: "#eff6ff" }, "&:last-child": { borderBottom: "none" },
                      }}>
                      <Box sx={{ width: 4, height: 44, bgcolor: st.dot, borderRadius: 2, flexShrink: 0, mt: 0.25 }} />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{name}</Typography>
                          {eventType && (
                            <Box sx={{ px: 0.75, py: 0.15, bgcolor: "#f1f5f9", borderRadius: 1 }}>
                              <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{eventType}</Typography>
                            </Box>
                          )}
                          <Box sx={{ px: 0.75, py: 0.15, bgcolor: st.bg, border: `1px solid ${st.border}`, borderRadius: 1 }}>
                            <Typography sx={{ fontSize: 11, color: st.color, fontWeight: 700 }}>{st.label}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#2f3891", fontFamily: "monospace", ml: "auto" }}>{lead.lead_ref}</Typography>
                        </Box>
                        {(lead.contact.route_pickup || lead.contact.route_dropoff) && (
                          <Typography sx={{ fontSize: 12, color: "#374151" }}>
                            {lead.contact.route_pickup}{lead.contact.route_dropoff ? ` → ${lead.contact.route_dropoff}` : ""}
                          </Typography>
                        )}
                        {lead.contact.email && <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{lead.contact.email}</Typography>}
                        {lead.contact.message && (
                          <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5, fontStyle: "italic" }} noWrap>{lead.contact.message}</Typography>
                        )}
                      </Box>
                    </Box>
                  );
                });
              })()}
            </Box>
          )}

          {/* ── List view ── */}
          {view === "list" && (
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden", bgcolor: "#ffffff" }}>
              {sortedListLeads.length === 0 ? (
                <Box sx={{ py: 8, textAlign: "center" }}>
                  <PersonOutlineIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                  <Typography sx={{ fontSize: 14, color: "#94a3b8", fontWeight: 600 }}>No enquiries found</Typography>
                  <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>Try adjusting your filters.</Typography>
                </Box>
              ) : (
                <>
                  {/* Section headers by month */}
                  {(() => {
                    const byMonth: Record<string, ILead[]> = {};
                    sortedListLeads.forEach((l) => {
                      const key = getLeadDate(l).format("MMMM YYYY");
                      if (!byMonth[key]) byMonth[key] = [];
                      byMonth[key].push(l);
                    });
                    return Object.entries(byMonth).map(([month, monthLeads]) => (
                      <Box key={month}>
                        <Box sx={{ px: 3, py: 0.75, bgcolor: "#f8fafc", borderBottom: `1px solid ${BORDER}`, borderTop: `1px solid ${BORDER}` }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            {month} · {monthLeads.length} enquir{monthLeads.length === 1 ? "y" : "ies"}
                          </Typography>
                        </Box>
                        {monthLeads.map((lead) => <ListRow key={lead.id} lead={lead} />)}
                      </Box>
                    ));
                  })()}
                </>
              )}
            </Box>
          )}
        </>
      )}

      {/* ── Status legend ── */}
      <Box sx={{ display: "flex", gap: 2, mt: 1.5, flexWrap: "wrap" }}>
        {(Object.entries(STATUS_CONFIG) as [LeadStatus, typeof STATUS_CONFIG[LeadStatus]][]).map(([s, cfg]) => (
          <Box key={s} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.dot }} />
            <Typography sx={{ fontSize: 11, color: "#64748b" }}>{cfg.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CalendarView;
